export type FormulaBinaryOp = "+" | "-" | "*" | "/";

export type FormulaFunctionName =
  | "min"
  | "max"
  | "abs"
  | "pow"
  | "sqrt"
  | "log"
  | "log10"
  | "exp"
  | "floor"
  | "ceil"
  | "round";

export const FORMULA_FUNCTION_NAMES: FormulaFunctionName[] = [
  "min",
  "max",
  "abs",
  "pow",
  "sqrt",
  "log",
  "log10",
  "exp",
  "floor",
  "ceil",
  "round",
];

export const FORMULA_FUNCTION_ARITY: Record<
  FormulaFunctionName,
  { min: number; max: number }
> = {
  min: { min: 2, max: Infinity },
  max: { min: 2, max: Infinity },
  pow: { min: 2, max: 2 },
  abs: { min: 1, max: 1 },
  sqrt: { min: 1, max: 1 },
  log: { min: 1, max: 1 },
  log10: { min: 1, max: 1 },
  exp: { min: 1, max: 1 },
  floor: { min: 1, max: 1 },
  ceil: { min: 1, max: 1 },
  round: { min: 1, max: 1 },
};

export type FormulaNode =
  | { type: "literal"; value: number }
  | { type: "signal"; signalId: string }
  | {
      type: "binary";
      op: FormulaBinaryOp;
      left: FormulaNode;
      right: FormulaNode;
    }
  | {
      type: "function";
      name: FormulaFunctionName;
      args: FormulaNode[];
    };

export type CandidateSourceId =
  | "exact_name"
  | "image_ann"
  | "es_fuzzy"
  | "shopping_list_cooccurrence"
  | "category_popular"
  | "global_popular"
  | "nearby_active"
  | "trending_items"
  | "interest_category"
  | "interest_promotion_tag"
  | "favorite_store"
  | "source_category_match"
  | "source_promotion_tag_match"
  | "source_merchant_match"
  | "source_flyer_list_match"
  | "popular_on_shopping_lists"
  | "geo_popular_on_shopping_lists"
  | "interest_category_popular"
  | "promotion_tags";

export type RecommendationEngineCommonSourceParams = {
  radiusKm?: number;
};

export type RecommendationEngineImageAnnSourceParams = {
  minImageScore?: number;
  onlyOnShoppingLists?: boolean;
};

export type RecommendationEngineCoOccurrenceSourceParams = {
  anchorCap?: number;
  anchorMinImageScore?: number;
  useExactNameAnchors?: boolean;
  useImageAnchors?: boolean;
  useEsFuzzyAnchors?: boolean;
  anchorsRequireOnShoppingLists?: boolean;
};

export type RecommendationEngineNearbySourceParams = {
  radiusKm?: number;
};

export type RecommendationEngineTrendingSourceParams = {
  hoursWindow?: number;
  radiusKm?: number;
};

export type RecommendationEngineInterestSourceParams = {
  maxMatches?: number;
};

/** The admin-chosen promotion tags a `promotion_tags` source recalls deals by. */
export type RecommendationEnginePromotionTagsSourceParams = {
  tagIds: string[];
};

type RecommendationEngineBaseSourceConfig<
  TSourceId extends CandidateSourceId,
  TParams = Record<never, never>,
> = {
  sourceId: TSourceId;
  enabled: boolean;
  cap: number;
  params?: RecommendationEngineCommonSourceParams & TParams;
};

export type RecommendationEngineSourceConfig =
  | RecommendationEngineBaseSourceConfig<"exact_name">
  | RecommendationEngineBaseSourceConfig<"image_ann", RecommendationEngineImageAnnSourceParams>
  | RecommendationEngineBaseSourceConfig<"es_fuzzy">
  | RecommendationEngineBaseSourceConfig<
      "shopping_list_cooccurrence",
      RecommendationEngineCoOccurrenceSourceParams
    >
  | RecommendationEngineBaseSourceConfig<"category_popular">
  | RecommendationEngineBaseSourceConfig<"global_popular">
  | RecommendationEngineBaseSourceConfig<"nearby_active", RecommendationEngineNearbySourceParams>
  | RecommendationEngineBaseSourceConfig<"trending_items", RecommendationEngineTrendingSourceParams>
  | RecommendationEngineBaseSourceConfig<
      "interest_category",
      RecommendationEngineInterestSourceParams
    >
  | RecommendationEngineBaseSourceConfig<
      "interest_promotion_tag",
      RecommendationEngineInterestSourceParams
    >
  | RecommendationEngineBaseSourceConfig<
      "favorite_store",
      RecommendationEngineInterestSourceParams
    >
  | RecommendationEngineBaseSourceConfig<"source_category_match">
  | RecommendationEngineBaseSourceConfig<"source_promotion_tag_match">
  | RecommendationEngineBaseSourceConfig<"source_merchant_match">
  | RecommendationEngineBaseSourceConfig<"source_flyer_list_match">
  | RecommendationEngineBaseSourceConfig<"popular_on_shopping_lists">
  | RecommendationEngineBaseSourceConfig<
      "geo_popular_on_shopping_lists",
      RecommendationEngineNearbySourceParams
    >
  | RecommendationEngineBaseSourceConfig<"interest_category_popular">
  | RecommendationEngineBaseSourceConfig<
      "promotion_tags",
      RecommendationEnginePromotionTagsSourceParams
    >;

export type RecommendationEngineFilterConfig =
  | { kind: "min_signal"; signalId: string; min: number }
  | { kind: "require_on_shopping_list" }
  | { kind: "exclude_source_merchant" }
  | { kind: "min_discount_percent"; minPercent: number }
  | { kind: "max_flyer_list_age_days"; maxAgeDays: number }
  | { kind: "exclude_flyer_item_ids"; flyerItemIds: string[] }
  | { kind: "require_deals_page_eligible" }
  | { kind: "suppress_recently_served"; lookbackHours: number };

export type RecommendationEngineFilterKind =
  RecommendationEngineFilterConfig["kind"];

export type RecommendationEngineDiversityDimension =
  | "merchant"
  | "category"
  | "store";

export const RECOMMENDATION_ENGINE_DIVERSITY_DIMENSIONS: RecommendationEngineDiversityDimension[] = [
  "merchant",
  "category",
  "store",
];

export type RecommendationEngineRerankRule =
  | {
      kind: "diversity_cap";
      dimension: RecommendationEngineDiversityDimension;
      maxPerGroup: number;
    }
  | {
      kind: "boost_flyer_item_ids";
      flyerItemIds: string[];
      multiplier: number;
    }
  | {
      kind: "demote_flyer_item_ids";
      flyerItemIds: string[];
      multiplier: number;
    };

export type RecommendationEngineRerankKind =
  RecommendationEngineRerankRule["kind"];

export type RecommendationEngineEndpointConfig = {
  version: number;
  displayLimit: number;
  rankingWindowLimit: number;
  excludeSourceMerchant: boolean;
  postProcess: { clamp01: boolean; round4dp: boolean };
  candidateSources: RecommendationEngineSourceConfig[];
  filters: RecommendationEngineFilterConfig[];
  rerankRules: RecommendationEngineRerankRule[];
  formula: FormulaNode;
  signalParams?: Record<string, { radiusKm?: number }>;
};

export type SignalGroup = string;

export type SignalMetadata = {
  id: string;
  label: string;
  summary?: string;
  description: string;
  howItIsCalculated?: string;
  dataUsed?: string[];
  relatedCandidateSources?: string[];
  formulaHint?: string;
  group: SignalGroup;
  valueRange: { min: number; max: number | null };
  isRaw: boolean;
  applicableEndpoints: RecommendationEngineEndpoint[];
};

export type CandidateSourceParamMetadata = {
  name: string;
  type: "number" | "boolean";
  required: boolean;
  description: string;
  defaultHint?: string;
};

export type CandidateSourceGroup = string;

export type CandidateSourceMetadata = {
  id: CandidateSourceId;
  label: string;
  group: CandidateSourceGroup;
  summary: string;
  description: string;
  howCandidatesAreFound: string;
  dataUsed: string[];
  requiredContext: string[];
  allowedEndpoints: RecommendationEngineEndpoint[];
  relatedSignals: string[];
  params: CandidateSourceParamMetadata[];
};

export type RecommendationEngineEndpoint =
  | "similar"
  | "recommended"
  | "nearby_picks"
  | "trending_now"
  | "browse_by_interest"
  | "smart_picks";
